import React, { createContext, useEffect, useState } from "react";
import getSymbolFromCurrency from "currency-symbol-map";
import moment from "moment-timezone";
import PropTypes from "prop-types";
import { clearAllBodyScrollLocks } from "body-scroll-lock";
import { useCookies } from "react-cookie";
import { globalHistory } from "@reach/router";
import { pushProductEvent } from "~utils/analytics";
import { fancyError } from "~utils/helpers";
import {
  getCheckoutURL,
  getInventoryLevelsByIds,
  getInventoryIdByVariantTitle
} from "~utils/shopify";
import { useTimeout } from "~utils/hooks";

export const AppContext = createContext({});

const AppProvider = ({ children }) => {
  const [activeCategories, setActiveCategories] = useState([`All`]);
  const [activeCurrency, setActiveCurrency] = useState(null);
  const [activeCurrencySymbol, setActiveCurrencySymbol] = useState(null);
  const [cart, setCart] = useState([]);
  const [cartActive, setCartActive] = useState(false);
  const [closedModalActive, setClosedModalActive] = useState(false);
  const [cookies, setCookie] = useCookies(null);
  const [cookiesAccepted, setCookiesAccepted] = useState(true);
  const [cookieMessageActive, setCookieMessageActive] = useState(false);
  const [deliveryModalActive, setDeliveryModalActive] = useState(false);
  const [deliveryModalSeen, setDeliveryModalSeen] = useState(false);
  const [inventoryCache, setInventoryCache] = useState(null);
  const [locationModalSeen, setLocationModalSeen] = useState(false);
  const [logoVisible, setLogoVisible] = useState(false);
  const [menuActive, setMenuActive] = useState(false);
  const [newsletterModalActive, setNewsletterModalActive] = useState(false);
  const [newsletterModalSeen, setNewsletterModalSeen] = useState(false);
  const [notificationActive, setNotificationActive] = useState(true);
  const [pathname, setPathname] = useState(null);
  const [subnavActive, setSubnavActive] = useState(false);
  const [subnavLink, setSubnavLink] = useState(null);
  const [subscriptionOverride, setSubscriptionOverride] = useState(false);
  const [toteDelivery, setToteDelivery] = useState(true);

  //

  const setDefaultCurrency = () => {
    let defaultCurrency;

    switch (process.env.GATSBY_REGION_CODE.toLowerCase()) {
      case `us`:
        defaultCurrency = `USD`;
        break;

      case `eu`:
        defaultCurrency = `EUR`;
        break;

      case `au`:
      default:
        defaultCurrency = `AUD`;
        break;
    }

    setActiveCurrency(defaultCurrency);
    setActiveCurrencySymbol(getSymbolFromCurrency(defaultCurrency));
  };

  //
  // onload

  useTimeout(() => {
    if (
      // typeof window !== `undefined` &&
      // typeof fetch !== `undefined` &&
      // !window.location.href.includes(`localhost`)
      typeof window !== `undefined` &&
      typeof fetch !== `undefined`
    ) {
      setNewsletterModalActive(true);

      // } else {
      // setNewsletterModalActive(true);
    }
  }, 2000);

  useEffect(() => {
    if (newsletterModalActive && !newsletterModalSeen) {
      setNewsletterModalSeen(true);
    }
  }, [newsletterModalActive]);

  useEffect(() => {
    if (window) {
      setPathname(window.location.pathname);
    }

    setDefaultCurrency();

    //
    // [cookie] cart

    if (!cookies?.accepted) {
      setCookieMessageActive(true);
    } else {
      setCookiesAccepted(true);

      if (cookies[`${process.env.GATSBY_REGION_CODE}_located`]) {
        setLocationModalSeen(true);
      }

      if (cookies?.[`${process.env.GATSBY_REGION_CODE}_cart`]) {
        const parsedCart = cookies[`${process.env.GATSBY_REGION_CODE}_cart`];

        let valid =
          Array.isArray(cookies[`${process.env.GATSBY_REGION_CODE}_cart`]) &&
          cookies?.[`${process.env.GATSBY_REGION_CODE}_cart`]?.[0];

        if (valid) {
          parsedCart.forEach(cookieCartItem => {
            if (!valid) {
              return;
            }

            if (
              typeof cookieCartItem === `undefined` ||
              cookieCartItem === null ||
              cookieCartItem === false ||
              cookieCartItem === `` ||
              !cookieCartItem?.quantity
            ) {
              valid = false;
            }
          });
        }

        if (!valid || process.env.GATSBY_RESET_COOKIES) {
          setCookie([`${process.env.GATSBY_REGION_CODE}_cart`], [], {
            path: `/`
          });
          setCart([]);
        } else {
          setCart(parsedCart);
        }
      }
    }

    return globalHistory.listen(({ location }) => {
      setPathname(location.pathname);
    });
  }, []);

  // pathname routing

  useEffect(() => {
    if (pathname) {
      clearAllBodyScrollLocks();

      if (pathname !== `/products` && pathname.includes(`/products/`)) {
        setLogoVisible(false);
      } else {
        setLogoVisible(true);
      }
    }
  }, [pathname]);

  // [cookie] acceptance

  useEffect(() => {
    if (cookiesAccepted) {
      setCookie(`accepted`, true, { path: `/` });

      if (cookieMessageActive) {
        setCookieMessageActive(false);
      }
    }
  }, [cookiesAccepted]);

  //

  const addToCart = (productWithVariant, quantity = 1) => {
    if (!productWithVariant?.variant) {
      return;
    }

    const melbourne = moment()
      .utc()
      .tz(`Australia/Melbourne`);
    const betwixt =
      (melbourne.day() === 5 && melbourne.hour() > 12) ||
      (melbourne.day() === 6 && melbourne.hour() < 12);

    let cutOff = false;

    if (!deliveryModalSeen && betwixt) {
      cutOff = true;
      setDeliveryModalActive(true);
    }

    const cartClone = JSON.parse(JSON.stringify(cart));

    let existingCartPosition = null;

    cartClone.forEach((cartItem, cartIndex) => {
      if (existingCartPosition !== null || cartItem?.subscription?.frequency) {
        return;
      }

      if (cartItem.variantId === productWithVariant.variant.id) {
        existingCartPosition = cartIndex;
      }
    });

    if (existingCartPosition === null) {
      cartClone.push({
        cutOff,
        quantity,
        variantId: productWithVariant.variant.id
      });
    } else {
      cartClone[existingCartPosition].quantity += quantity;
    }

    setCartActive(true);
    setCart(cartClone);

    if (cookiesAccepted) {
      setCookie(`${process.env.GATSBY_REGION_CODE}_cart`, cartClone, {
        path: `/`
      });

      pushProductEvent(
        productWithVariant,
        activeCurrency,
        `addToCart`,
        quantity
      );
    }
  };

  const addSubscriptionToCart = variantId => {
    const melbourne = moment()
      .utc()
      .tz(`Australia/Melbourne`);
    const betwixt =
      (melbourne.day() === 5 && melbourne.hour() > 12) ||
      (melbourne.day() === 6 && melbourne.hour() < 12);

    let cutOff = false;

    if (!deliveryModalSeen && betwixt) {
      cutOff = true;
      setDeliveryModalActive(true);
    }

    const cartClone = JSON.parse(JSON.stringify(cart));

    let existingCartPosition = null;

    cartClone.forEach((cartItem, cartIndex) => {
      if (existingCartPosition !== null || cartItem?.subscription?.frequency) {
        return;
      }

      if (cartItem.variantId === variantId) {
        existingCartPosition = cartIndex;
      }
    });

    if (existingCartPosition) {
      fancyError(`Subscription already exists`);
      return;
    }

    cartClone.push({
      cutOff,
      quantity: 1,
      subscription: true,
      variantId
    });

    setCartActive(true);
    setCart(cartClone);

    if (cookiesAccepted) {
      setCookie(`${process.env.GATSBY_REGION_CODE}_cart`, cartClone, {
        path: `/`
      });
    }
  };

  const checkout = notes => {
    if (
      !process.env.GATSBY_SHOPIFY_STORE ||
      process.env.GATSBY_SHOPIFY_STORE === ``
    ) {
      fancyError(`Shopify environment variables have not been defined.`);

      return;
    }

    setCartActive(false);

    getCheckoutURL(cart, activeCurrency, notes || ``).then(response => {
      response.json().then(({ data }) => {
        if (data?.checkoutCreate?.checkout?.webUrl) {
          let { webUrl } = data.checkoutCreate.checkout;

          if (webUrl.includes(`wildwood-bakery-preston.myshopify.com`)) {
            webUrl = webUrl.replace(
              `wildwood-bakery-preston.myshopify.com`,
              `orders.wildwoodbakery.com.au`
            );
          }

          window.location.href = webUrl;
        }
      });
    });
  };

  const decreaseQuantityByCartIndex = cartIndex => {
    if (!cart?.[cartIndex]) {
      return;
    }

    const cartClone = JSON.parse(JSON.stringify(cart));

    if (cartClone[cartIndex].quantity <= 1) {
      cartClone.splice(cartIndex, 1);
    } else {
      cartClone[cartIndex].quantity -= 1;
    }

    setCart(cartClone);

    if (cookiesAccepted) {
      setCookie([`${process.env.GATSBY_REGION_CODE}_cart`], cartClone, {
        path: `/`
      });

      pushProductEvent(cartClone[cartIndex], activeCurrency, `removeFromCart`);
    }
  };

  const increaseQuantityByCartIndex = cartIndex => {
    if (!cart?.[cartIndex]) {
      return;
    }

    const cartClone = JSON.parse(JSON.stringify(cart));

    cartClone[cartIndex].quantity += 1;

    setCart(cartClone);

    if (cookiesAccepted) {
      setCookie([`${process.env.GATSBY_REGION_CODE}_cart`], cartClone, {
        path: `/`
      });

      pushProductEvent(cartClone[cartIndex], activeCurrency, `addToCart`);
    }
  };

  const removeFromCartByIndex = cartIndex => {
    if (!cart?.[cartIndex]) {
      return;
    }

    const cartClone = JSON.parse(JSON.stringify(cart));

    cartClone.splice(cartIndex, 1);

    setCart(cartClone);

    if (cookiesAccepted) {
      setCookie([`${process.env.GATSBY_REGION_CODE}_cart`], cartClone, {
        path: `/`
      });

      pushProductEvent(
        cart[cartIndex],
        activeCurrency,
        `removeFromCart`,
        cart[cartIndex].quantity
      );
    }
  };

  const refreshInventoryCache = (products, adminProducts) => {
    if (
      typeof window === `undefined` ||
      typeof fetch === `undefined` ||
      window.location.href.includes(`localhost`) ||
      window.location.href.includes(`192.`)
    ) {
      setInventoryCache({});
      return;
    }

    const variantsByInventoryId = {};

    products.forEach(product => {
      product.variants.forEach(variant => {
        const inventoryId = getInventoryIdByVariantTitle(
          adminProducts.edges,
          product.handle,
          variant.title
        );

        if (inventoryId && inventoryId !== ``) {
          variantsByInventoryId[inventoryId] = variant;
        }
      });
    });

    getInventoryLevelsByIds(Object.keys(variantsByInventoryId).join()).then(
      response => {
        response.json().then(inventory => {
          const newInventoryCache = {};

          if (!inventory?.inventory_levels?.[0]) {
            setInventoryCache(newInventoryCache);
            return;
          }

          inventory.inventory_levels.forEach(inventoryLevel => {
            const variant =
              variantsByInventoryId[inventoryLevel.inventory_item_id];

            if (typeof inventoryLevel.available === `undefined`) {
              newInventoryCache[variant.id] = 0;

              return;
            }

            newInventoryCache[variant.id] = inventoryLevel.available;
          });

          setInventoryCache(newInventoryCache);
        });
      }
    );
  };

  useEffect(() => {
    if (locationModalSeen) {
      setCookie([`${process.env.GATSBY_REGION_CODE}_located`], true, {
        path: `/`
      });
    }
  }, [locationModalSeen]);

  //

  return (
    <AppContext.Provider
      value={{
        activeCategories,
        setActiveCategories,
        cart,
        setCart,
        cartActive,
        setCartActive,
        closedModalActive,
        setClosedModalActive,
        activeCurrencySymbol,
        setActiveCurrencySymbol,
        activeCurrency,
        setActiveCurrency,
        cookiesAccepted,
        setCookiesAccepted,
        cookieMessageActive,
        setCookieMessageActive,
        deliveryModalActive,
        setDeliveryModalActive,
        deliveryModalSeen,
        setDeliveryModalSeen,
        subnavActive,
        setSubnavActive,
        subnavLink,
        setSubnavLink,
        locationModalSeen,
        setLocationModalSeen,
        logoVisible,
        setLogoVisible,
        menuActive,
        setMenuActive,
        newsletterModalActive,
        setNewsletterModalActive,
        newsletterModalSeen,
        setNewsletterModalSeen,
        notificationActive,
        setNotificationActive,
        pathname,
        subscriptionOverride,
        setSubscriptionOverride,
        toteDelivery,
        setToteDelivery,
        //
        addToCart,
        addSubscriptionToCart,
        checkout,
        decreaseQuantityByCartIndex,
        increaseQuantityByCartIndex,
        removeFromCartByIndex,
        inventoryCache,
        refreshInventoryCache
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

AppProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default AppProvider;
